/* ============================================
   usePWAInstall — PWA installation detection & prompt
   Detects mobile, standalone mode, and handles
   the beforeinstallprompt event for Android.
   ============================================ */
import { useState, useEffect, useCallback, useRef } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWAInstall() {
  const [isMobile, setIsMobile] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [canInstall, setCanInstall] = useState(false)
  const [justInstalled, setJustInstalled] = useState(false)
  const [ready, setReady] = useState(false)
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    // Detect mobile via viewport width + touch
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 ||
        ('ontouchstart' in window && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
      setIsMobile(mobile)
    }

    // Detect standalone (PWA installed and opened from home screen)
    const checkStandalone = () => {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true || // iOS Safari
        document.referrer.includes('android-app://') // TWA
      setIsStandalone(standalone)
    }

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    setIsIOS(ios)

    checkMobile()
    checkStandalone()

    // Mark as ready after initial detection
    setReady(true)

    // Listen for resize
    window.addEventListener('resize', checkMobile)

    // Listen for display-mode changes
    const mql = window.matchMedia('(display-mode: standalone)')
    const handleDisplayChange = () => checkStandalone()
    mql.addEventListener('change', handleDisplayChange)

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      deferredPrompt.current = e as BeforeInstallPromptEvent
      setCanInstall(true)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall)

    // Listen for appinstalled — app was installed but we're still in the browser.
    // Do NOT set isStandalone here; the user must open the installed app.
    const handleInstalled = () => {
      deferredPrompt.current = null
      setCanInstall(false)
      setJustInstalled(true)
    }
    window.addEventListener('appinstalled', handleInstalled)

    return () => {
      window.removeEventListener('resize', checkMobile)
      mql.removeEventListener('change', handleDisplayChange)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt.current) return false
    try {
      await deferredPrompt.current.prompt()
      const { outcome } = await deferredPrompt.current.userChoice
      if (outcome === 'accepted') {
        deferredPrompt.current = null
        setCanInstall(false)
        setJustInstalled(true)
        return true
      }
    } catch {
      // Install prompt failed
    }
    return false
  }, [])

  // The main gate condition: mobile + not standalone
  const needsInstall = isMobile && !isStandalone

  return {
    isMobile,
    isStandalone,
    isIOS,
    canInstall,
    needsInstall,
    justInstalled,
    promptInstall,
    ready,
  }
}

